import {
  Box,
  Typography,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SpeedIcon from '@mui/icons-material/Speed';
import PersonIcon from '@mui/icons-material/Person';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ErrorIcon from '@mui/icons-material/Error';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { chartTooltipProps } from '@/lib/chartTheme';
import type { AgentPerformanceData, AgentPerformanceTrend } from '@/types';
import { dateFnsLocale, formatNumber } from '@/utils/locale';

interface AgentPerformanceSectionProps {
  agentPerformanceData: AgentPerformanceData;
  agentPerformanceTrend: AgentPerformanceTrend[];
  isMobile: boolean;
  colors: string[];
}

export function AgentPerformanceSection({
  agentPerformanceData,
  agentPerformanceTrend,
  isMobile,
  colors,
}: AgentPerformanceSectionProps) {
  const { t } = useTranslation('reports');
  const theme = useTheme();

  return (
    <Grid size={{ xs: 12 }}>
      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" fontWeight={600}>
            {t('agentPerformance')}
          </Typography>
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 2, border: 1, borderColor: 'primary.200', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <SmartToyIcon sx={{ color: 'primary.main' }} />
                <Typography variant="caption" color="text.secondary">
                  {t('interactions')}
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight={700} color="primary.main">
                {formatNumber(agentPerformanceData.totalInteractions)}
              </Typography>
              {agentPerformanceData.performanceChange > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
                  <Typography variant="caption" color="success.main">
                    +{agentPerformanceData.performanceChange.toFixed(1)}% {t('vsPrevious')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{ p: 2, bgcolor: 'success.50', borderRadius: 2, border: 1, borderColor: 'success.200', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PsychologyIcon sx={{ color: 'success.main' }} />
                <Typography variant="caption" color="text.secondary">
                  {t('accuracy')}
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight={700} color="success.main">
                {agentPerformanceData.intentAccuracy.toFixed(1)}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('recognitionConfidence')}
              </Typography>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{ p: 2, bgcolor: 'info.50', borderRadius: 2, border: 1, borderColor: 'info.200', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <SpeedIcon sx={{ color: 'info.main' }} />
                <Typography variant="caption" color="text.secondary">
                  {t('responseTime')}
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight={700} color="info.main">
                {agentPerformanceData.avgResponseTime.toFixed(1)}s
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('averageLabel')}
              </Typography>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{ p: 2, bgcolor: 'warning.50', borderRadius: 2, border: 1, borderColor: 'warning.200', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PersonIcon sx={{ color: 'warning.main' }} />
                <Typography variant="caption" color="text.secondary">
                  {t('handoffRate')}
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight={700} color="warning.main">
                {agentPerformanceData.handoffRate.toFixed(1)}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('requiresHumanHelp')}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              {t('agentBreakdown')}
            </Typography>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('agent')}</TableCell>
                    <TableCell align="right">{t('interactionsCount')}</TableCell>
                    <TableCell align="right">{t('successRate')}</TableCell>
                    <TableCell align="right">{t('responseTime')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {agentPerformanceData.agentBreakdown.map((agent) => (
                    <TableRow key={agent.agentType} hover>
                      <TableCell>
                        <Typography variant="body2">{agent.agentType}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={agent.interactions} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500} color={agent.successRate >= 95 ? 'success.main' : 'text.primary'}>
                          {agent.successRate.toFixed(1)}%
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{agent.avgResponseTime.toFixed(1)}s</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              {t('performanceTrend')}
            </Typography>
            <Box sx={{ p: 2 }}>
              <ResponsiveContainer width="100%" height={isMobile ? 180 : 250}>
                <LineChart
                  data={agentPerformanceTrend}
                  margin={isMobile ? { top: 5, right: 10, left: 0, bottom: 5 } : { top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(value) => format(new Date(value), 'd.M.', { locale: dateFnsLocale() })}
                  />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip
                    {...chartTooltipProps(theme)}
                    labelFormatter={(value) => format(new Date(value as string), 'd.M.yyyy', { locale: dateFnsLocale() })}
                    formatter={(value: number) => value.toFixed(1)}
                  />
                  <Legend />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="successRate"
                    stroke={theme.palette.success.main}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    name={t('successRate')}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="avgResponseTime"
                    stroke={theme.palette.primary.main}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    name={t('responseTimeSeconds')}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              {t('intentDistribution')}
            </Typography>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('intent')}</TableCell>
                    <TableCell align="right">{t('count')}</TableCell>
                    <TableCell align="right">{t('successRate')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {agentPerformanceData.intentDistribution.map((intent, index) => (
                    <TableRow key={intent.intent} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: colors[index % colors.length],
                            }}
                          />
                          <Typography variant="body2">{intent.intent}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={intent.count} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500}>
                          {intent.successRate.toFixed(1)}%
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ErrorIcon sx={{ fontSize: 18, color: 'error.main' }} />
              {t('topErrors')}
            </Typography>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableBody>
                  {agentPerformanceData.topErrors.map((error, index) => (
                    <TableRow key={`${error.error}-${index}`} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 24, height: 24, fontSize: 12, bgcolor: 'error.light' }}>
                            {index + 1}
                          </Avatar>
                          <Box>
                            <Typography variant="body2">{error.error}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {error.agentType}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={error.count} size="small" color="error" variant="outlined" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
}
